// /setup — the node's first run: create the OWNER with the one-time code that
// erp-core prints to its log at boot while no local user exists
// (POST /v1/erp/auth/bootstrap). A PUBLIC, pre-login page (no shell), reached
// from the node gate when GET /v1/erp/auth/status says `bootstrap_required`.
// Success = the owner is signed in (the answer is a session, same shape as
// /auth/login) and lands on the home page — or on /activate first when the
// node has no licence yet (the gate decides).
//
// No board draws this page (deploy/node/README step 5 did it with curl); it
// reuses the activation card. Copy is PROPOSED (OPEN-QUESTIONS «node first run»).
//
// States: idle · submitting (button loading) · code (wrong / incomplete code) ·
// field (name / business / phone / PIN refused) · already (another tab or
// person created the owner → sign in) · error (offline / failed page message).

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PrimaryAction } from '@/components/Button';
import { Field, TextInput } from '@/components/Form';
import { HOME_PATH } from '@/app/Forbidden';
import { PinInput, PIN_LENGTH } from '@/app/PinInput';
import { adoptLocalSession, sessionStore } from '@/lib/auth';
import { normalizeIraqiPhone } from '@/lib/digits';
import { ME_QUERY_KEY } from '@/lib/me';
import { codeChars, CODE_LENGTH, formatGroupedCode } from '../lib/code';
import {
  AUTH_STATUS_KEY,
  bootstrapOwner,
  FIRST_RUN_COPY,
  firstRunError,
  isNotANode,
  useLicenceStatus,
  useNodeAuthStatus,
  type FirstRunField,
  type NodeAuthStatus,
} from '../data/api';
import '../system.css';

export const FIRST_RUN_TEXT = {
  title: 'الإعداد الأول لهذا الجهاز',
  sub: 'أنشئ حساب المالك. بعدها يدخل كل من في المحل من أي جهاز على الشبكة برقم الهاتف والرمز السري.',
  code: 'رمز الإعداد الأول',
  codePlaceholder: 'XXXX-XXXX-XXXX',
  codeHint: 'يظهر في سجل الخادم عند كل تشغيل حتى يُنشأ حساب المالك:',
  codeCommand: 'docker compose logs erp-core | grep bootstrap_code',
  business: 'اسم النشاط',
  licensedTo: 'النشاط',
  name: 'اسم المالك',
  phone: 'رقم الهاتف',
  pin: 'الرمز السري',
  pinConfirm: 'أعد إدخال الرمز السري',
  cta: 'إنشاء حساب المالك',
  busy: 'جارٍ الإنشاء…',
} as const;

/** The rule erp-core's ValidatePIN applies: not one repeated digit, not a straight run (wrapping 9→0). */
export function isWeakPin(pin: string): boolean {
  if (!/^\d{6}$/.test(pin)) return false;
  let same = true;
  let up = true;
  let down = true;
  for (let i = 1; i < pin.length; i++) {
    const d = pin.charCodeAt(i) - pin.charCodeAt(i - 1);
    same &&= d === 0;
    up &&= d === 1 || d === -9;
    down &&= d === -1 || d === 9;
  }
  return same || up || down;
}

type ErrorKey = FirstRunField | 'pinConfirm';
type Errors = Partial<Record<ErrorKey, string>>;

const settled = (prev: NodeAuthStatus | undefined): NodeAuthStatus => ({
  mode: 'node',
  idle_lock_seconds: 0,
  ...prev,
  bootstrap_required: false,
});

export function FirstRunScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice;
  const qc = useQueryClient();
  const authStatus = useNodeAuthStatus();
  const licence = useLicenceStatus();
  const [code, setCode] = useState('');
  const [business, setBusiness] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [pageError, setPageError] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const titleId = useId();

  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  // Not a node (cloud: 404), or the owner already exists: carry on.
  if (!done && !busy) {
    if (authStatus.isError && isNotANode(authStatus.error)) return <Navigate to="/auth/login" replace />;
    if (authStatus.data && !authStatus.data.bootstrap_required) {
      return <Navigate to={sessionStore.isAuthenticated() ? HOME_PATH : '/auth/login'} replace />;
    }
  }

  // erp-core puts the owner on the licence's business when the node is
  // already licensed: the business name is then the licensee's, not asked.
  const licensee = licence.data?.licensed ? licence.data.licensee : null;
  const askBusiness = !licensee;

  const clear = (key: ErrorKey) => {
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    if (pageError) setPageError(null);
  };

  const validate = (): Errors => {
    const e: Errors = {};
    if (codeChars(code).length !== CODE_LENGTH) e.code = code.trim() ? FIRST_RUN_COPY.codeIncomplete : FIRST_RUN_COPY.codeInvalid;
    if (askBusiness && !business.trim()) e.business_name = FIRST_RUN_COPY.businessRequired;
    if (!name.trim()) e.name = FIRST_RUN_COPY.nameRequired;
    if (!normalizeIraqiPhone(phone)) e.phone = FIRST_RUN_COPY.phoneInvalid;
    if (pin.length !== PIN_LENGTH) e.pin = FIRST_RUN_COPY.pinFormat;
    else if (isWeakPin(pin)) e.pin = FIRST_RUN_COPY.pinWeak;
    else if (pin2 !== pin) e.pinConfirm = FIRST_RUN_COPY.pinMismatch;
    return e;
  };

  const submit = async (ev?: FormEvent) => {
    ev?.preventDefault();
    if (busy) return;
    setPageError(null);
    const e = validate();
    setErrors(e);
    if (Object.values(e).some(Boolean)) {
      if (e.code) codeRef.current?.focus();
      return;
    }
    const e164 = normalizeIraqiPhone(phone) as string;
    setBusy(true);
    try {
      const session = await bootstrapOwner({
        code: formatGroupedCode(code),
        phone: e164,
        name: name.trim(),
        pin,
        ...(askBusiness ? { business_name: business.trim() } : {}),
      });
      setDone(true);
      qc.setQueryData<NodeAuthStatus>(AUTH_STATUS_KEY, settled);
      adoptLocalSession(session, e164);
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      navigate(HOME_PATH, { replace: true });
    } catch (err) {
      const fe = firstRunError(err);
      if (fe.already) {
        setDone(true);
        qc.setQueryData<NodeAuthStatus>(AUTH_STATUS_KEY, settled);
        navigate('/auth/login', { replace: true, state: { notice: fe.message } });
        return;
      }
      if (fe.field) {
        setErrors({ [fe.field]: fe.message });
        if (fe.field === 'code') codeRef.current?.focus();
        if (fe.field === 'pin') {
          setPin('');
          setPin2('');
        }
      } else {
        setPageError(fe.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const hasFieldError = Object.values(errors).some(Boolean);
  const state = busy ? 'submitting' : pageError ? 'error' : errors.code ? 'code' : hasFieldError ? 'field' : 'idle';

  return (
    <div className="e-center sys-act-page" data-testid="first-run-screen" data-state={state}>
      <form className="sys-act-card sys-fr-card" onSubmit={submit} noValidate aria-labelledby={titleId}>
        <img className="sys-act-mark" src="/brand/logo-ehjizli.svg" alt="" aria-hidden="true" />
        <div>
          <h1 id={titleId} className="sys-act-title">
            {FIRST_RUN_TEXT.title}
          </h1>
          <p className="sys-act-sub">{FIRST_RUN_TEXT.sub}</p>
        </div>
        {notice && !pageError && (
          <p role="status" className="sys-fr-notice" data-testid="first-run-notice">
            {notice}
          </p>
        )}
        <Field
          label={FIRST_RUN_TEXT.code}
          error={errors.code}
          hint={
            <>
              {FIRST_RUN_TEXT.codeHint}{' '}
              <bdi dir="ltr" className="ltr sys-fr-cmd">
                {FIRST_RUN_TEXT.codeCommand}
              </bdi>
            </>
          }
        >
          {(id) => (
            <TextInput
              id={id}
              ref={codeRef}
              ltr
              className="sys-act-code"
              placeholder={FIRST_RUN_TEXT.codePlaceholder}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              value={code}
              invalid={!!errors.code}
              readOnly={busy}
              data-testid="first-run-code"
              onChange={(e) => {
                setCode(formatGroupedCode(e.target.value));
                clear('code');
              }}
            />
          )}
        </Field>
        {askBusiness ? (
          <Field label={FIRST_RUN_TEXT.business} error={errors.business_name}>
            {(id) => (
              <TextInput
                id={id}
                value={business}
                maxLength={80}
                invalid={!!errors.business_name}
                readOnly={busy}
                onChange={(e) => {
                  setBusiness(e.target.value);
                  clear('business_name');
                }}
              />
            )}
          </Field>
        ) : (
          <p className="sys-fr-licensee" data-testid="first-run-licensee">
            {`${FIRST_RUN_TEXT.licensedTo}: «${licensee}»`}
          </p>
        )}
        <Field label={FIRST_RUN_TEXT.name} error={errors.name}>
          {(id) => (
            <TextInput
              id={id}
              value={name}
              maxLength={80}
              autoComplete="name"
              invalid={!!errors.name}
              readOnly={busy}
              onChange={(e) => {
                setName(e.target.value);
                clear('name');
              }}
            />
          )}
        </Field>
        <Field label={FIRST_RUN_TEXT.phone} error={errors.phone}>
          {(id) => (
            <TextInput
              id={id}
              ltr
              inputMode="tel"
              autoComplete="tel"
              placeholder="07XX XXX XXXX"
              value={phone}
              invalid={!!errors.phone}
              readOnly={busy}
              onChange={(e) => {
                setPhone(e.target.value);
                clear('phone');
              }}
            />
          )}
        </Field>
        <Field label={FIRST_RUN_TEXT.pin} error={errors.pin}>
          {() => (
            <PinInput
              value={pin}
              onChange={(v) => {
                setPin(v);
                clear('pin');
              }}
              label={FIRST_RUN_TEXT.pin}
              disabled={busy}
              invalid={!!errors.pin}
            />
          )}
        </Field>
        <Field label={FIRST_RUN_TEXT.pinConfirm} error={errors.pinConfirm}>
          {() => (
            <PinInput
              value={pin2}
              onChange={(v) => {
                setPin2(v);
                clear('pinConfirm');
              }}
              label={FIRST_RUN_TEXT.pinConfirm}
              disabled={busy}
              invalid={!!errors.pinConfirm}
            />
          )}
        </Field>
        <PrimaryAction type="submit" size="lg" block loading={busy} data-testid="first-run-submit">
          {busy ? FIRST_RUN_TEXT.busy : FIRST_RUN_TEXT.cta}
        </PrimaryAction>
        {pageError && (
          <p className="sys-act-alert" role="alert" data-testid="first-run-alert">
            {pageError}
          </p>
        )}
      </form>
    </div>
  );
}

export default FirstRunScreen;
